import { useEffect, type MutableRefObject, useCallback } from 'react'
import type { GeoJSONSource } from 'mapbox-gl'
// @ts-expect-error - turf types issue
import * as turf from '@turf/turf'
import type { MapRef } from 'react-map-gl'
import {
  convertFromGeoJSON,
  convertToGeoJSON,
  ensurePolygonClosed,
} from '../../../utils/mapHelpers'

interface UseGridCellParams {
  mapRef: MutableRefObject<MapRef | null>
  drawingMode: 'draw' | 'grid' | 'view'
  editingZoneId?: string | null
  editMode?: 'polygon' | 'grid' | null
  gridSize: number
  gridCellPosition: [number, number] | null
  generateGridCell: (center: [number, number], size: number) => GeoJSON.Feature
  gridCellSourceId: string
  gridCellLayerId: string
  readonly onAreaCalculated?: (area: number) => void
  readonly onAreaCalculatingChange?: (isCalculating: boolean) => void
  lastCalculatedDistanceRef: MutableRefObject<number>
}

export const useGridCell = ({
  mapRef,
  drawingMode,
  editingZoneId,
  editMode,
  gridSize,
  gridCellPosition,
  generateGridCell,
  gridCellSourceId,
  gridCellLayerId,
  onAreaCalculated,
  onAreaCalculatingChange,
  lastCalculatedDistanceRef,
}: UseGridCellParams) => {
  const addOrUpdateSource = useCallback(
    (map: mapboxgl.Map, gridCell: GeoJSON.Feature) => {
      const source = map.getSource(gridCellSourceId) as GeoJSONSource
      const data = { type: 'FeatureCollection' as const, features: [gridCell] }

      if (!source) {
        try {
          map.addSource(gridCellSourceId, { type: 'geojson', data })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating grid cell source:', error)
        }
      } else {
        try {
          source.setData(data)
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error updating grid cell source:', error)
        }
      }
    },
    [gridCellSourceId]
  )

  const addOrUpdateLayers = useCallback(
    (map: mapboxgl.Map) => {
      if (!map.getLayer(gridCellLayerId)) {
        try {
          map.addLayer({
            id: gridCellLayerId,
            type: 'fill',
            source: gridCellSourceId,
            paint: { 'fill-color': '#ff6b35', 'fill-opacity': 0.3 },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating fill layer:', error)
        }
      } else {
        map.setPaintProperty(gridCellLayerId, 'fill-color', '#ff6b35')
      }

      const outlineId = `${gridCellLayerId}-outline`
      if (!map.getLayer(outlineId)) {
        try {
          map.addLayer({
            id: outlineId,
            type: 'line',
            source: gridCellSourceId,
            paint: {
              'line-color': '#ff6b35',
              'line-width': 2,
              'line-dasharray': [2, 2],
            },
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error creating outline layer:', error)
        }
      } else {
        map.setPaintProperty(outlineId, 'line-color', '#ff6b35')
      }
    },
    [gridCellLayerId, gridCellSourceId]
  )

  const calculateArea = useCallback(
    (gridCell: GeoJSON.Feature) => {
      if (gridCell.geometry.type === 'Polygon' && onAreaCalculated) {
        onAreaCalculatingChange?.(true)
        const areaSqM = turf.area(turf.polygon(gridCell.geometry.coordinates))
        onAreaCalculated(areaSqM / 1000000)
        onAreaCalculatingChange?.(false)
      }
    },
    [onAreaCalculated, onAreaCalculatingChange]
  )

  const updateGridCell = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    if (!gridCellPosition) {
      const source = map.getSource(gridCellSourceId) as GeoJSONSource
      if (source) source.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    const gridCell = generateGridCell(gridCellPosition, gridSize)
    addOrUpdateSource(map, gridCell)
    addOrUpdateLayers(map)
    calculateArea(gridCell)
  }, [
    mapRef,
    gridCellPosition,
    gridSize,
    generateGridCell,
    gridCellSourceId,
    addOrUpdateSource,
    addOrUpdateLayers,
    calculateArea,
  ])

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return undefined

    const shouldShowGrid =
      drawingMode === 'grid' || (editingZoneId && editMode === 'grid')
    if (!shouldShowGrid) return undefined

    if (!map.isStyleLoaded()) {
      map.once('style.load', () => {
        setTimeout(() => {
          if (mapRef.current && drawingMode === 'grid') updateGridCell()
        }, 100)
      })
      return
    }

    updateGridCell()

    const onStyleData = () => {
      if (
        shouldShowGrid &&
        map.isStyleLoaded() &&
        !map.getLayer(gridCellLayerId)
      ) {
        updateGridCell()
      }
    }
    map.on('styledata', onStyleData)

    return () => {
      map.off('styledata', onStyleData)
    }
  }, [
    drawingMode,
    editingZoneId,
    editMode,
    gridCellLayerId,
    mapRef,
    updateGridCell,
  ])

  useEffect(() => {
    if (!mapRef.current) return
    const map = mapRef.current.getMap()
    const shouldShowGrid =
      drawingMode === 'grid' || (editingZoneId && editMode === 'grid')
    if (!shouldShowGrid) {
      const outlineId = `${gridCellLayerId}-outline`
      if (map.getLayer(gridCellLayerId)) {
        map.removeLayer(gridCellLayerId)
      }
      if (map.getLayer(outlineId)) {
        map.removeLayer(outlineId)
      }
      if (map.getSource(gridCellSourceId)) {
        map.removeSource(gridCellSourceId)
      }
    }
  }, [
    drawingMode,
    editingZoneId,
    editMode,
    gridCellSourceId,
    gridCellLayerId,
    mapRef,
  ])

  useEffect(() => {
    if (drawingMode !== 'grid') {
      window._drawControlGetGridCell = undefined as unknown as () => {
        coordinates: number[][]
        area: number
      } | null
      return
    }

    window._drawControlGetGridCell = () => {
      if (!mapRef.current || !gridCellPosition) return null

      const map = mapRef.current.getMap()
      const source = map.getSource(gridCellSourceId) as GeoJSONSource
      if (!source) return null

      const data = source._data as GeoJSON.FeatureCollection
      const feature = data?.features?.[0]
      if (!feature || feature.geometry.type !== 'Polygon') return null

      const coordinates = feature.geometry.coordinates[0] || []
      if (coordinates.length === 0) return null

      let frontendCoords = convertFromGeoJSON(coordinates)
      frontendCoords = ensurePolygonClosed(frontendCoords)

      const geoJsonCoords = convertToGeoJSON(frontendCoords)
      const polygon = turf.polygon([geoJsonCoords])
      const area = turf.area(polygon)

      return {
        coordinates: frontendCoords,
        area: Math.round(area),
      }
    }

    return () => {
      window._drawControlGetGridCell = undefined as unknown as () => {
        coordinates: number[][]
        area: number
      } | null
    }
  }, [
    drawingMode,
    gridCellPosition,
    gridCellSourceId,
    mapRef,
    lastCalculatedDistanceRef,
  ])
}
